import { useCallback, useEffect, useReducer } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import qs from 'query-string';

import { useAuth, usePost } from 'hooks';
import { Button, Loader } from 'components/common';
import {
  buildKnowledgeBaseUrl,
  createKnowledgeBaseErrorLog,
} from 'utils/knowledgeBase';
import { log } from 'services/api';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 300px;
  text-align: center;
`;

const Message = styled.p`
  margin-top: 16px;
  color: ${p => (p.theme.darkMode ? '#333' : '#666')};
  font-size: 16px;
`;

const ErrorMessage = styled.p`
  margin-top: 16px;
  color: #d32f2f;
  font-size: 16px;
  font-weight: 500;
`;

// Reducer for knowledge base redirect flow
const initialState = {
  status: 'idle', // idle -> validating -> refreshing -> redirecting -> error
  error: null,
  hmacToken: null,
  originalHmacToken: null,
  articleSlug: null,
  timeoutId: null,
};

const knowledgeBaseReducer = (state, action) => {
  switch (action.type) {
    case 'START_REDIRECT':
      return {
        ...state,
        status: 'validating',
        articleSlug: action.payload,
        error: null,
      };
    case 'VALIDATION_SUCCESS':
      return {
        ...state,
        status: 'refreshing',
        originalHmacToken: action.payload,
      };
    case 'SET_TIMEOUT':
      return {
        ...state,
        timeoutId: action.payload,
      };
    case 'CLEAR_TIMEOUT':
      if (state.timeoutId) {
        clearTimeout(state.timeoutId);
      }
      return {
        ...state,
        timeoutId: null,
      };
    case 'REFRESH_TIMEOUT':
      if (state.timeoutId) {
        clearTimeout(state.timeoutId);
      }
      return {
        ...state,
        status: 'error',
        error: action.payload,
        timeoutId: null,
      };
    case 'REFRESH_SUCCESS':
      if (state.timeoutId) {
        clearTimeout(state.timeoutId);
      }
      return {
        ...state,
        status: 'redirecting',
        hmacToken: action.payload,
        timeoutId: null,
      };
    case 'REDIRECT_SUCCESS':
      return { ...state, status: 'completed' };
    case 'ERROR':
      if (state.timeoutId) {
        clearTimeout(state.timeoutId);
      }
      return {
        ...state,
        status: 'error',
        error: action.payload,
        timeoutId: null,
      };
    case 'RESET':
      if (state.timeoutId) {
        clearTimeout(state.timeoutId);
      }
      return initialState;
    default:
      return state;
  }
};

const KnowledgeBase = () => {
  const { articleSlug } = useParams();
  const { user, isAuthed, pending, checkUser } = useAuth();
  const { push } = useHistory();
  const { pathname } = useLocation();
  const [, postLog] = usePost({ url: log });
  const [state, dispatch] = useReducer(knowledgeBaseReducer, initialState);

  const dispatchError = useCallback(
    (displayMsg, logMsg) => {
      const errorLogData = createKnowledgeBaseErrorLog(
        state.articleSlug,
        'Failure',
        logMsg
      );
      postLog(errorLogData);
      dispatch({ type: 'ERROR', payload: displayMsg });
    },
    [state.articleSlug, postLog, dispatch]
  );

  // Start the redirect process
  const startRedirect = useCallback(() => {
    if (articleSlug) {
      dispatch({ type: 'START_REDIRECT', payload: articleSlug });
    }
  }, [articleSlug]);

  // Handle unauthenticated users - redirect to login
  useEffect(() => {
    if (pending) return;

    if (!isAuthed) {
      const queryParams = qs.stringify(
        { redirect: pathname },
        { encode: false }
      );
      push(`/login?${queryParams}`);
      return;
    }

    // Start redirect flow for authenticated users
    if (isAuthed && user && articleSlug && state.status === 'idle') {
      startRedirect();
    }
  }, [
    isAuthed,
    user,
    articleSlug,
    pending,
    push,
    pathname,
    state.status,
    startRedirect,
  ]);

  // Handle validation phase
  useEffect(() => {
    if (state.status === 'validating' && user) {
      // Check if user has required data
      if (!user.userEmail) {
        return dispatchError(
          "We couldn't authenticate you",
          'No user email address found in user session'
        );
      }

      if (!user.hmac) {
        return dispatchError(
          "We couldn't authenticate you",
          'No HMAC token found in user session'
        );
      }

      // Validation passed, store original token and move to refresh
      dispatch({ type: 'VALIDATION_SUCCESS', payload: user.hmac });
    }
  }, [state.status, user, dispatchError]);

  // Handle refresh phase - trigger checkUser
  useEffect(() => {
    if (state.status !== 'refreshing') return;
    if (typeof checkUser !== 'function') {
      return dispatchError(
        "We couldn't refresh your authentication",
        'checkUser function not available'
      );
    }
    checkUser();
  }, [state.status, checkUser, dispatchError]);

  // Handle refresh timeout
  useEffect(() => {
    if (state.status === 'refreshing') {
      const timeoutId = setTimeout(() => {
        const errorLogData = createKnowledgeBaseErrorLog(
          state.articleSlug,
          'Timeout',
          'Token refresh timed out after 15 seconds'
        );
        postLog(errorLogData);
        dispatch({
          type: 'REFRESH_TIMEOUT',
          payload: 'Authentication refresh timed out. Please try again.',
        });
      }, 15000);

      dispatch({ type: 'SET_TIMEOUT', payload: timeoutId });

      return () => {
        clearTimeout(timeoutId);
        dispatch({ type: 'CLEAR_TIMEOUT' });
      };
    }
  }, [state.status, state.articleSlug, postLog]);

  // Listen for HMAC token changes after refresh
  useEffect(() => {
    if (state.status !== 'refreshing') return;
    if (user?.hmac && state.originalHmacToken) {
      // Check if the HMAC token has changed (indicating successful refresh)
      if (user.hmac !== state.originalHmacToken) {
        dispatch({
          type: 'REFRESH_SUCCESS',
          payload: user.hmac,
        });
      }
      // If tokens are the same, we're still waiting for the refresh to complete
    }
  }, [state.status, state.originalHmacToken, user?.hmac]);

  // Handle redirect phase
  useEffect(() => {
    if (state.status === 'redirecting' && state.hmacToken && user) {
      try {
        const hmacData = {
          username: user.userEmail,
          hmac: state.hmacToken,
        };

        const kbUrl = buildKnowledgeBaseUrl(
          state.articleSlug,
          encodeURIComponent(JSON.stringify(hmacData))
        );

        window.location.href = kbUrl;

        dispatch({ type: 'REDIRECT_SUCCESS' });
      } catch (error) {
        dispatchError(
          "We couldn't build the redirect URL",
          `URL construction failed: ${error.message}`
        );
      }
    }
  }, [state.status, state.hmacToken, state.articleSlug, user, dispatchError]);

  const handleRetry = () => {
    dispatch({ type: 'RESET' });
  };

  // Show error state
  if (state.status === 'error') {
    return (
      <Container>
        <ErrorMessage>{state.error}</ErrorMessage>
        <Button
          type='secondary'
          onClick={handleRetry}
          style={{ marginTop: '16px' }}
        >
          Try Again
        </Button>
      </Container>
    );
  }

  // Show loading/redirecting state
  const getLoadingMessage = () => {
    switch (state.status) {
      case 'validating':
        return 'Validating authentication...';
      case 'refreshing':
        return 'Refreshing token...';
      case 'redirecting':
        return 'Redirecting to Knowledge Base...';
      case 'completed':
        return 'Redirecting...';
      default:
        return 'Loading...';
    }
  };

  return (
    <Container>
      <Loader />
      <Message>{getLoadingMessage()}</Message>
    </Container>
  );
};

export default KnowledgeBase;
